import { useEffect, useState } from "react";
import { httpClient, httpClient3 } from "../../../util/Api";
import { useDispatch } from "react-redux";
import jwt_decode from "jwt-decode";
import { setAuthenticUser } from "../../../appRedux/actions/Common";
import _ from "lodash";
import { message as toastNotification } from "antd";

export const unAuthUser = "User Not Verified";

export const isAuthoriedUser = (user) => user.User.Username !== unAuthUser;

const encryptPassword = (password) => {
  const key = "SSA-SOFT-2024";
  let encrypted = "";

  for (let i = 0; i < password.length; i++) {
    encrypted += String.fromCharCode(
      password.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    );
  }

  return btoa(encrypted);
};

export const useProvideAuth = () => {
  const user = JSON.parse(localStorage.getItem("authUser"));
  const [authUser, setAuthUser] = useState(
    !_.isEmpty(user) && isAuthoriedUser(user) ? user : null,
  );
  const [error, setError] = useState("");
  const [isLoadingUser, setLoadingUser] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setLoadingUser(false);
    if (authUser) {
      setIsLoggedIn(true);
      dispatch(setAuthenticUser(authUser));
      localStorage.setItem("token", authUser.token);
      localStorage.setItem("user", JSON.stringify(jwt_decode(authUser.token)));
      localStorage.setItem("authUser", JSON.stringify(authUser));
      httpClient.defaults.headers.common["Authorization"] =
        "Bearer " + authUser.token;
      httpClient.defaults.withCredentials = true;
      httpClient3.defaults.headers.common["Authorization"] =
        "Bearer " + authUser.token;
      httpClient3.defaults.withCredentials = true;
    }
  }, [authUser]);

  const fetchStart = () => {
    setLoading(true);
    setError("");
  };

  const fetchSuccess = () => {
    setLoading(false);
    setError("");
  };

  const fetchError = (error) => {
    setLoading(false);
    setError(error);
  };

  const userLogin = (user, callbackFun) => {
    fetchStart();

    const encryptedPassword = encryptPassword(user.password);

    httpClient
      .get(
        `Login?username=${user.userName}&password=${encodeURIComponent(encryptedPassword)}`,
        {
          withCredentials: true,
        },
      )
      .then(({ data }) => {
        fetchSuccess();

        if (data?.User?.IsValidLogin === true) {
          setAuthUser(data);
          localStorage.setItem("authUser", JSON.stringify(data));
          dispatch(setAuthenticUser(data));
          const userName = data.User.EmpName || data.User.Username;
          toastNotification.success(`Welcome back, ${userName}!`, 3);
        } else {
          const errorMsg =
            data?.User?.Username || "Invalid username or password";
          toastNotification.error(errorMsg, 3);
        }

        if (callbackFun) callbackFun();
      })
      .catch(function (error) {
        fetchError(error.message);
        toastNotification.error("Login failed. Please try again.", 3);
      });
  };

  const userSignup = (user, callbackFun) => {
    fetchStart();
    httpClient
      .post("auth/register", {
        ...user,
        password: encryptPassword(user.password),
      })
      .then(({ data }) => {
        if (data.result) {
          fetchSuccess();
          localStorage.setItem("token", data.token.access_token);
          httpClient.defaults.headers.common["Authorization"] =
            "Bearer " + data.token.access_token;
          getAuthUser();
          if (callbackFun) callbackFun();
        } else {
          fetchError(data.error);
        }
      })
      .catch(function (error) {
        fetchError(error.message);
      });
  };

  const sendPasswordResetEmail = (email, callbackFun) => {
    fetchStart();
    setTimeout(() => {
      fetchSuccess();
      if (callbackFun) callbackFun();
    }, 300);
  };

  const confirmPasswordReset = (code, password, callbackFun) => {
    fetchStart();
    setTimeout(() => {
      fetchSuccess();
      if (callbackFun) callbackFun();
    }, 300);
  };

  const renderSocialMediaLogin = () => null;

  const userSignOut = (callbackFun) => {
    fetchStart();
    localStorage.removeItem("token");
    fetchSuccess();
    if (callbackFun) callbackFun();
  };

  const getAuthUser = () => {
    fetchStart();
    httpClient
      .get(`Customer?customerId=${localStorage.getItem("id")}`)
      .then(({ data }) => {
        if (data.HasErrors === false) {
          fetchSuccess();
          setAuthUser(data);
        } else {
          fetchError(data);
        }
      })
      .catch(function (error) {
        httpClient.defaults.headers.common["Authorization"] = "";
        fetchError(error.message);
      });
  };

  const getLogout = () => {
    fetchStart();
    httpClient
      .get(`Logout`)
      .then(({ data }) => {
        if (data === "Session Is Cleared") {
          fetchSuccess();
          setIsLoggedIn(false);
          setAuthUser(null);
          dispatch(setAuthenticUser(null));
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("authUser");
          httpClient.defaults.headers.common["Authorization"] = "";
        } else {
          fetchError(data);
        }
      })
      .catch(function (error) {
        setIsLoggedIn(false);
        setAuthUser(null);
        dispatch(setAuthenticUser(null));
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("authUser");
        httpClient.defaults.headers.common["Authorization"] = "";
      });
  };

  return {
    isLoadingUser,
    isLoading,
    authUser,
    isLoggedIn,
    error,
    setError,
    setAuthUser,
    getAuthUser,
    userLogin,
    userSignup,
    userSignOut,
    renderSocialMediaLogin,
    sendPasswordResetEmail,
    confirmPasswordReset,
    getLogout,
  };
};
